import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProducts } from '../services/api';
import { 
    FaArrowRight, 
    FaStar, 
    FaTruck, 
    FaShieldAlt, 
    FaUndo, 
    FaGift, 
    FaUsers,
    FaRocket,
    FaPlay,
    FaQuoteLeft,
    FaChevronLeft,
    FaChevronRight
} from 'react-icons/fa';

const HomePage = () => {
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [newArrivals, setNewArrivals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [currentTestimonial, setCurrentTestimonial] = useState(0);

    const heroSlides = [
        {
            title: "Ignite Their Imagination",
            subtitle: "Discover toys that inspire creativity and learning",
            image: "https://images.pexels.com/photos/3661351/pexels-photo-3661351.jpeg?auto=compress&cs=tinysrgb&w=1200",
            cta: "Shop Now",
            link: "/products"
        },
        {
            title: "Quality Toys, Happy Kids",
            subtitle: "Safe, durable, and educational toys for every age",
            image: "https://images.pexels.com/photos/4145190/pexels-photo-4145190.jpeg?auto=compress&cs=tinysrgb&w=1200",
            cta: "Explore Collection",
            link: "/products"
        },
        {
            title: "Special Holiday Deals",
            subtitle: "Up to 40% off on selected items",
            image: "https://images.pexels.com/photos/3661080/pexels-photo-3661080.jpeg?auto=compress&cs=tinysrgb&w=1200",
            cta: "Shop Deals",
            link: "/products"
        }
    ];

    const testimonials = [
        {
            name: "Sarah Johnson",
            role: "Mother of 2",
            content: "TFM Toys has become our go-to for birthday gifts. The quality is outstanding and my kids absolutely love every toy we've purchased!",
            rating: 5,
            image: "https://randomuser.me/api/portraits/women/1.jpg"
        },
        {
            name: "Michael Chen",
            role: "Grandfather",
            content: "Finding safe, educational toys for my grandchildren has never been easier. The customer service is exceptional and shipping is fast!",
            rating: 5,
            image: "https://randomuser.me/api/portraits/men/2.jpg"
        },
        {
            name: "Emma Williams",
            role: "Teacher",
            content: "I use TFM Toys products in my classroom. They're durable, engaging, and perfect for early childhood development.",
            rating: 5,
            image: "https://randomuser.me/api/portraits/women/3.jpg"
        }
    ];

    const categories = [
        { name: "Action Figures", icon: "🦸", color: "from-red-500 to-orange-500", items: 45 },
        { name: "Educational Toys", icon: "📚", color: "from-blue-500 to-cyan-500", items: 38 },
        { name: "Soft Toys", icon: "🧸", color: "from-pink-500 to-rose-500", items: 52 },
        { name: "Building Sets", icon: "🏗️", color: "from-green-500 to-emerald-500", items: 31 },
        { name: "Board Games", icon: "🎲", color: "from-purple-500 to-indigo-500", items: 27 },
        { name: "Outdoor Toys", icon: "⚽", color: "from-yellow-500 to-amber-500", items: 24 }
    ];

    const benefits = [
        { icon: FaTruck, title: "Free Shipping", desc: "On orders over £50", color: "blue" },
        { icon: FaShieldAlt, title: "2-Year Warranty", desc: "On all products", color: "green" },
        { icon: FaUndo, title: "Easy Returns", desc: "30-day return policy", color: "purple" },
        { icon: FaGift, title: "Gift Wrapping", desc: "Free gift wrap available", color: "pink" }
    ];

    useEffect(() => {
        fetchProducts();
        const slideInterval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 5000);
        return () => clearInterval(slideInterval);
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await getProducts({ featured: true });
            setFeaturedProducts(response.products.slice(0, 4));
            setNewArrivals(response.products.slice(4, 8));
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    };

    const nextTestimonial = () => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    };

    const prevTestimonial = () => {
        setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    };

    return (
        <div className="overflow-hidden">
            {/* Hero Carousel */}
            <div className="relative h-screen min-h-[600px] max-h-[800px]">
                {heroSlides.map((slide, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 transition-opacity duration-1000 ${
                            index === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
                        }`}
                    >
                        <div 
                            className="absolute inset-0 bg-cover bg-center"
                            style={{ backgroundImage: `url(${slide.image})` }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
                        </div>
                        <div className="relative h-full flex items-center">
                            <div className="container mx-auto px-4">
                                <div className="max-w-2xl text-white">
                                    <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up">
                                        {slide.title}
                                    </h1>
                                    <p className="text-xl md:text-2xl mb-8 opacity-90 animate-fade-in-up animation-delay-200">
                                        {slide.subtitle}
                                    </p>
                                    <Link 
                                        to={slide.link}
                                        className="inline-flex items-center gap-2 bg-purple-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-purple-700 transition transform hover:scale-105 animate-fade-in-up animation-delay-400"
                                    >
                                        {slide.cta} <FaArrowRight />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                
                {/* Carousel Controls */}
                <button 
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm p-3 rounded-full hover:bg-white/30 transition"
                >
                    <FaChevronLeft className="text-white text-2xl" />
                </button>
                <button 
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm p-3 rounded-full hover:bg-white/30 transition"
                >
                    <FaChevronRight className="text-white text-2xl" />
                </button>
                
                {/* Dots */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
                    {heroSlides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`w-3 h-3 rounded-full transition-all ${
                                index === currentSlide ? 'bg-white w-8' : 'bg-white/50'
                            }`}
                        />
                    ))}
                </div>
            </div>

            {/* Features Banner */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 py-12">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {benefits.map((benefit, index) => (
                            <div key={index} className="text-center text-white transform hover:scale-105 transition">
                                <benefit.icon className="text-3xl mx-auto mb-3" />
                                <h3 className="font-semibold mb-1">{benefit.title}</h3>
                                <p className="text-sm opacity-90">{benefit.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Categories Section */}
            <div className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Shop by Category
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Find the perfect toy for every interest and age group
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {categories.map((category, index) => (
                            <Link 
                                key={index}
                                to={`/products?category=${category.name}`}
                                className="group relative overflow-hidden rounded-2xl shadow-lg transform hover:scale-105 transition duration-300"
                            >
                                <div className={`h-64 bg-gradient-to-r ${category.color} relative overflow-hidden`}>
                                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition"></div>
                                    <div className="absolute top-6 left-6 text-6xl">{category.icon}</div>
                                    <div className="absolute bottom-6 left-6 right-6">
                                        <h3 className="text-2xl font-bold text-white mb-1">{category.name}</h3>
                                        <p className="text-white/90">{category.items} items</p>
                                    </div>
                                    <div className="absolute bottom-6 right-6 transform translate-x-full group-hover:translate-x-0 transition">
                                        <FaArrowRight className="text-white text-2xl" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>

            {/* Featured Products */}
            <div className="py-20">
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-end mb-12">
                        <div>
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                                Featured Products
                            </h2>
                            <p className="text-xl text-gray-600">
                                Our most loved toys this season
                            </p>
                        </div>
                        <Link to="/products" className="hidden md:flex items-center gap-2 text-purple-600 font-semibold hover:gap-3 transition">
                            View All <FaArrowRight />
                        </Link>
                    </div>
                    
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {featuredProducts.map((product, index) => (
                                <div key={product.id} className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                                    <Link to={`/products/${product.id}`}>
                                        <div className="relative h-64 overflow-hidden">
                                            {product.image_url ? (
                                                <img 
                                                    src={product.image_url} 
                                                    alt={product.name}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://picsum.photos/id/20/500/500';
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                                                    <span className="text-6xl">🧸</span>
                                                </div>
                                            )}
                                            {index === 0 && (
                                                <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                                    Best Seller
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-6">
                                            <h3 className="font-semibold text-lg text-gray-800 mb-2 line-clamp-1">
                                                {product.name}
                                            </h3>
                                            <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                                                {product.description}
                                            </p>
                                            <div className="flex items-center justify-between">
                                                <span className="text-2xl font-bold text-purple-600">
                                                    £{product.price}
                                                </span>
                                                <div className="flex items-center gap-1 text-yellow-400">
                                                    <FaStar />
                                                    <FaStar />
                                                    <FaStar />
                                                    <FaStar />
                                                    <FaStar />
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Video/Story Section */}
            <div className="relative py-32 bg-fixed bg-cover bg-center" style={{ backgroundImage: "url('https://images.pexels.com/photos/3661080/pexels-photo-3661080.jpeg?auto=compress&cs=tinysrgb&w=1200')" }}>
                <div className="absolute inset-0 bg-black/60"></div>
                <div className="relative container mx-auto px-4 text-center text-white">
                    <div className="max-w-3xl mx-auto">
                        <div className="inline-block p-4 bg-white/20 backdrop-blur rounded-full mb-6">
                            <FaPlay className="text-4xl" />
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">
                            The Magic of Play
                        </h2>
                        <p className="text-xl mb-8">
                            Watch how our toys bring joy and learning to children everywhere
                        </p>
                        <button className="bg-purple-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-purple-700 transition transform hover:scale-105">
                            Watch Our Story
                        </button>
                    </div>
                </div>
            </div>

            {/* New Arrivals */}
            <div className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            New Arrivals
                        </h2>
                        <p className="text-xl text-gray-600">
                            Fresh from our collection, just for you
                        </p>
                    </div>
                    
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {newArrivals.map((product) => (
                                <div key={product.id} className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-300">
                                    <Link to={`/products/${product.id}`}>
                                        <div className="relative h-56 overflow-hidden">
                                            {product.image_url ? (
                                                <img 
                                                    src={product.image_url} 
                                                    alt={product.name}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://picsum.photos/id/30/500/500';
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                                                    <span className="text-6xl">🎁</span>
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                                                <span className="bg-white text-purple-600 px-4 py-2 rounded-full font-semibold">
                                                    Quick View
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-4">
                                            <h3 className="font-semibold text-gray-800 mb-1 line-clamp-1">
                                                {product.name}
                                            </h3>
                                            <div className="flex items-center justify-between">
                                                <span className="text-xl font-bold text-purple-600">
                                                    £{product.price}
                                                </span>
                                                <span className="text-sm text-green-600">New</span>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Testimonials */}
            <div className="py-20 bg-gradient-to-r from-purple-600 to-pink-600">
                <div className="container mx-auto px-4">
                    <div className="text-center text-white mb-12">
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">
                            What Parents Say
                        </h2>
                        <p className="text-xl opacity-90">
                            Join thousands of happy families
                        </p>
                    </div>
                    
                    <div className="relative max-w-4xl mx-auto">
                        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl">
                            <FaQuoteLeft className="text-6xl text-purple-200 mb-6" />
                            <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                                {testimonials[currentTestimonial].content}
                            </p>
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200">
                                    <img 
                                        src={testimonials[currentTestimonial].image} 
                                        alt={testimonials[currentTestimonial].name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        {testimonials[currentTestimonial].name}
                                    </h4>
                                    <p className="text-gray-600">
                                        {testimonials[currentTestimonial].role}
                                    </p>
                                    <div className="flex text-yellow-400 mt-1">
                                        {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                                            <FaStar key={i} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <button 
                            onClick={prevTestimonial}
                            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition"
                        >
                            <FaChevronLeft className="text-purple-600" />
                        </button>
                        <button 
                            onClick={nextTestimonial}
                            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition"
                        >
                            <FaChevronRight className="text-purple-600" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            <div className="py-20">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        <div className="text-center transform hover:scale-105 transition">
                            <div className="text-5xl font-bold text-purple-600 mb-2">50K+</div>
                            <div className="text-gray-600">Happy Customers</div>
                        </div>
                        <div className="text-center transform hover:scale-105 transition">
                            <div className="text-5xl font-bold text-purple-600 mb-2">1000+</div>
                            <div className="text-gray-600">Products Sold</div>
                        </div>
                        <div className="text-center transform hover:scale-105 transition">
                            <div className="text-5xl font-bold text-purple-600 mb-2">4.9</div>
                            <div className="text-gray-600">Customer Rating</div>
                        </div>
                        <div className="text-center transform hover:scale-105 transition">
                            <div className="text-5xl font-bold text-purple-600 mb-2">24/7</div>
                            <div className="text-gray-600">Customer Support</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Banner */}
            <div className="py-20 bg-gray-900">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Start Your Toy Adventure?
                    </h2>
                    <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                        Join thousands of happy families who trust TFM Toys for quality and joy
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link 
                            to="/products"
                            className="bg-purple-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-purple-700 transition transform hover:scale-105"
                        >
                            Shop Now
                        </Link>
                        <Link 
                            to="/contact"
                            className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-gray-900 transition transform hover:scale-105"
                        >
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage;